import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api';
import { saveRecentMeeting } from '@/utils/storage';
import type { Meeting, Participant, ScreenShare } from '@/types';

interface UseMeetingOptions {
  meetingId: string;
  participantName: string;
  isHost?: boolean;
  onMeetingEnded?: () => void;
  onParticipantJoined?: (participant: Participant) => void;
  onParticipantLeft?: (participantId: string) => void;
  onScreenShareStarted?: (share: ScreenShare) => void;
  onScreenShareStopped?: (participantId: string) => void;
}

interface UseMeetingReturn {
  meeting: Meeting | null;
  participants: Participant[];
  activeScreenShares: ScreenShare[];
  currentParticipant: Participant | null;
  authToken: string | null;
  realtimeKitMeetingId: string | null;
  isLoading: boolean;
  isConnected: boolean;
  error: string | null;
  joinMeeting: () => Promise<void>;
  leaveMeeting: () => Promise<void>;
  endMeeting: () => Promise<void>;
  toggleScreenShare: (action: 'start' | 'stop') => Promise<void>;
  refreshMeetingInfo: () => Promise<void>;
  isHost: boolean;
  canScreenShare: boolean;
  screenShareCount: number;
  participantCount: number;
}

export function useMeeting(options: UseMeetingOptions): UseMeetingReturn {
  const {
    meetingId,
    participantName,
    isHost = false,
    onMeetingEnded,
    onParticipantJoined,
    onParticipantLeft,
    onScreenShareStarted,
    onScreenShareStopped,
  } = options;

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [activeScreenShares, setActiveScreenShares] = useState<ScreenShare[]>([]);
  const [currentParticipant, setCurrentParticipant] = useState<Participant | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [realtimeKitMeetingId, setRealtimeKitMeetingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasJoined, setHasJoined] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLeftRef = useRef(false);
  const isConnectingWSRef = useRef(false);
  const isMountedRef = useRef(true);

  const handleWebSocketMessage = useCallback((message: any) => {
    switch (message.type) {
      case 'meeting-state':
        setParticipants(message.data.participants);
        setActiveScreenShares(message.data.activeScreenShares);
        break;
      case 'participant-joined':
        setParticipants(prev => {
          if (prev.some(p => p.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
        onParticipantJoined?.(message.data);
        break;
      case 'participant-left':
        setParticipants(prev => prev.filter(p => p.id !== message.participantId));
        onParticipantLeft?.(message.participantId);
        break;
      case 'screen-share-started':
        setActiveScreenShares(prev => [...prev, message.data]);
        setParticipants(prev =>
          prev?.map(p =>
            p.id === message.participantId ? { ...p, isScreenSharing: true } : p
          )
        );
        onScreenShareStarted?.(message.data);
        break;
      case 'screen-share-stopped':
        setActiveScreenShares(prev =>
          prev.filter(s => s.participantId !== message.participantId)
        );
        setParticipants(prev =>
          prev.map(p =>
            p.id === message.participantId ? { ...p, isScreenSharing: false } : p
          )
        );
        onScreenShareStopped?.(message.participantId);
        break;
      case 'meeting-ended':
        onMeetingEnded?.();
        break;
      case 'meeting-updated':
        console.log('meeting updated via websocket:',message.data)
        setMeeting(prev=> prev ? {...prev , ...message.data}: null);
        break
    }
  }, [onParticipantJoined, onParticipantLeft, onScreenShareStarted, onScreenShareStopped, onMeetingEnded]);

  const connectWebSocket = useCallback(() => {
    if (!currentParticipant || isConnectingWSRef.current || !isMountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    isConnectingWSRef.current = true;

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const wsUrl = baseUrl.replace('http', 'ws');
      const ws = new WebSocket(
        `${wsUrl}/api/meetings/${meetingId}/ws?participantId=${currentParticipant.id}`
      );

      ws.onopen = () => {
        console.log('WebSocket connected');
        if (isMountedRef.current) {
          setIsConnected(true);
          setError(null);
        }
        isConnectingWSRef.current = false;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code);
        if (isMountedRef.current) {
          setIsConnected(false);
        }
        isConnectingWSRef.current = false;

        // Only reconnect if not a normal closure and still mounted
        if (event.code !== 1000 && hasJoined && !hasLeftRef.current && isMountedRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Reconnecting WebSocket...');
            connectWebSocket();
          }, 2000);
        }
      };

      ws.onerror = () => {
        if (isMountedRef.current) {
          setError('WebSocket connection error');
        }
        isConnectingWSRef.current = false;
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      isConnectingWSRef.current = false;
    }
  }, [currentParticipant, meetingId, hasJoined, handleWebSocketMessage]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) return;

    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, 30000);
  }, []);

  const cleanupConnection = useCallback(() => {
    // Clear timers
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close(1000, 'Cleanup');
      wsRef.current = null;
    }
  }, []);

  const joinMeeting = useCallback(async () => {
    if (hasJoined) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.joinMeeting(meetingId, {
        participantName,
        isHost,
      });

      if (response.success && response.data) {
        setCurrentParticipant(response.data.participant);
        setAuthToken(response.data.authToken);
        setRealtimeKitMeetingId(response.data.realtimeKitMeetingId);
        setHasJoined(true);
        hasLeftRef.current = false; // Reset on join

        await refreshMeetingInfo();

        if (meeting?.title) {
          saveRecentMeeting(meetingId, meeting.title);
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to join meeting';
      setError(errorMsg);
      console.error('Join meeting error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [meetingId, participantName, isHost, hasJoined, meeting?.title]);

  const leaveMeeting = useCallback(async () => {
    if (!currentParticipant || hasLeftRef.current) return;

    hasLeftRef.current = true;

    try {
      cleanupConnection();
      await apiClient.leaveMeeting(meetingId, currentParticipant.id);
      setHasJoined(false);
      setIsConnected(false);
    } catch (err) {
      console.error('Leave meeting error:', err);
    }
  }, [meetingId, currentParticipant, cleanupConnection]);

  const endMeeting = useCallback(async () => {
    if (!currentParticipant?.permissions.isHost) {
      throw new Error('Only host can end the meeting');
    }

    try {
      hasLeftRef.current = true;
      cleanupConnection();
      await apiClient.endMeeting(meetingId, currentParticipant.id);
    } catch (err) {
      console.error('End meeting error:', err);
      throw err;
    }
  }, [meetingId, currentParticipant, cleanupConnection]);

  const toggleScreenShare = useCallback(async (action: 'start' | 'stop') => {
    if (!currentParticipant) {
      throw new Error('Not joined to meeting');
    }

    await apiClient.toggleScreenShare(meetingId, {
      participantId: currentParticipant.id,
      action,
    });
  }, [meetingId, currentParticipant]);

  const refreshMeetingInfo = useCallback(async () => {
    try {
      const response = await apiClient.getMeeting(meetingId);

      if (response.success && response.data) {
        setMeeting(response.data.meeting);
        setRealtimeKitMeetingId(response.data.realtimeKitMeetingId || null);
        setParticipants(response.data.participants);
        setActiveScreenShares(response.data.activeScreenShares);
      }
    } catch (err) {
      console.error('Refresh meeting info error:', err);
    }
  }, [meetingId]);

  // Connect WebSocket when participant joins
  useEffect(() => {
    if (currentParticipant && hasJoined && !wsRef.current) {
      // Wait for participant to be persisted in DO
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          connectWebSocket();
          startHeartbeat();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentParticipant, hasJoined, connectWebSocket, startHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      console.log('🧹 Cleanup triggered');
      isMountedRef.current = false;

      // Stop reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Close WebSocket
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close(1000, 'Component unmounted');
      }

      // Leave meeting if still joined
      if (hasJoined && currentParticipant && !hasLeftRef.current) {
        console.log('🚪 Leaving meeting on unmount:', currentParticipant.id);
        hasLeftRef.current = true;
        apiClient.leaveMeeting(meetingId, currentParticipant.id).catch(console.error);
      }
    };
  }, [hasJoined, currentParticipant, meetingId]);

  const isHostUser = currentParticipant?.permissions.isHost ?? false;
  const canScreenShare = currentParticipant?.permissions.canScreenShare ?? false;

  return {
    meeting,
    participants,
    activeScreenShares,
    currentParticipant,
    authToken,
    realtimeKitMeetingId,
    isLoading,
    isConnected,
    error,
    joinMeeting,
    leaveMeeting,
    endMeeting,
    toggleScreenShare,
    refreshMeetingInfo,
    isHost: isHostUser,
    canScreenShare,
    screenShareCount: activeScreenShares?.length,
    participantCount: participants?.length,
  };
}